import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { Platform } from 'react-native';

export interface ConversationMessage {
  id: string;
  conversation_id: string;
  sender_type: 'homeowner' | 'manager' | 'ai';
  sender_id: string | null;
  content: string;
  attachment_url: string | null;
  attachment_name: string | null;
  attachment_size: number | null;
  attachment_type: string | null;
  read_by_homeowner_at: string | null;
  read_by_manager_at: string | null;
  created_at: string;
  sender?: {
    id: string;
    full_name: string;
    profile_photo_url: string | null;
  };
}

export function useConversationMessages(conversationId: string | null) {
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchMessages = useCallback(async () => {
    if (!conversationId) {
      setMessages([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // First, get the conversation to check if it's HoneyBot and get thread_id
      const { data: conversation } = await supabase
        .from('conversations')
        .select('type, thread_id, homeowner_id')
        .eq('id', conversationId)
        .single();

      // Fetch messages from conversation_messages table
      const { data: newMessages, error: fetchError } = await supabase
        .from('conversation_messages')
        .select(`
          *,
          sender:profiles!conversation_messages_sender_id_fkey (
            id,
            full_name,
            profile_photo_url
          )
        `)
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (fetchError) throw fetchError;

      let allMessages: ConversationMessage[] = newMessages || [];

      // If this is a HoneyBot conversation and has a thread_id, also fetch historical messages
      if (conversation?.type === 'honeybot' && conversation.thread_id) {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user && conversation.homeowner_id === user.id) {
          // Fetch historical messages from assistant_messages
          const { data: historicalMessages } = await supabase
            .from('assistant_messages')
            .select('*')
            .eq('thread_id', conversation.thread_id)
            .order('created_at', { ascending: true });

          if (historicalMessages && historicalMessages.length > 0) {
            // Convert historical messages to ConversationMessage format
            const convertedMessages: ConversationMessage[] = historicalMessages.map(msg => ({
              id: `historical-${msg.id}`,
              conversation_id: conversationId,
              sender_type: msg.role === 'user' ? 'homeowner' : 'ai',
              sender_id: msg.role === 'user' ? conversation.homeowner_id : null,
              content: msg.content,
              read_by_homeowner_at: null,
              read_by_manager_at: null,
              created_at: msg.created_at
            }));

            // Merge messages, avoiding duplicates
            // If a message exists in both, prefer the one from conversation_messages
            // Use a more sophisticated deduplication: content + sender_type + timestamp within 1 second
            const messageMap = new Map<string, ConversationMessage>();
            
            // Add historical messages first
            convertedMessages.forEach(msg => {
              // Create a key based on content, sender type, and timestamp (rounded to nearest second)
              const timestamp = new Date(msg.created_at).getTime();
              const roundedTime = Math.floor(timestamp / 1000) * 1000;
              const key = `${msg.sender_type}-${msg.content.substring(0, 50)}-${roundedTime}`;
              
              // Only add if we don't already have a message from conversation_messages with similar content/time
              const hasDuplicate = (newMessages || []).some(newMsg => {
                const newTimestamp = new Date(newMsg.created_at).getTime();
                const newRoundedTime = Math.floor(newTimestamp / 1000) * 1000;
                return (
                  newMsg.sender_type === msg.sender_type &&
                  newMsg.content === msg.content &&
                  Math.abs(newRoundedTime - roundedTime) < 2000 // Within 2 seconds
                );
              });

              if (!hasDuplicate) {
                messageMap.set(key, msg);
              }
            });

            // Add new messages (they take precedence and won't be overridden)
            (newMessages || []).forEach(msg => {
              const timestamp = new Date(msg.created_at).getTime();
              const roundedTime = Math.floor(timestamp / 1000) * 1000;
              const key = `${msg.sender_type}-${msg.content.substring(0, 50)}-${roundedTime}`;
              messageMap.set(key, msg);
            });

            // Convert back to array and sort by created_at
            allMessages = Array.from(messageMap.values()).sort((a, b) => 
              new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
            );
          }
        }
      }

      setMessages(allMessages);
      setError(null);
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  useEffect(() => {
    fetchMessages();

    if (!conversationId) return;

    // Only set up realtime on web platform
    if (Platform.OS === 'web') {
      const channel = supabase
        .channel(`conversation-messages-${conversationId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'conversation_messages',
            filter: `conversation_id=eq.${conversationId}`
          },
          (payload) => {
            console.log('New message received:', payload);
            
            // Fetch the complete message with sender info
            supabase
              .from('conversation_messages')
              .select(`
                *,
                sender:profiles!conversation_messages_sender_id_fkey (
                  id,
                  full_name,
                  profile_photo_url
                )
              `)
              .eq('id', payload.new.id)
              .single()
              .then(({ data }) => {
                if (data) {
                  setMessages(prev => {
                    // Check if message already exists to prevent duplicates
                    const exists = prev.some(msg => msg.id === data.id);
                    if (exists) {
                      return prev;
                    }
                    // Add new message and sort by created_at
                    const updated = [...prev, data];
                    return updated.sort((a, b) => 
                      new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
                    );
                  });
                }
              });
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'conversation_messages',
            filter: `conversation_id=eq.${conversationId}`
          },
          (payload) => {
            console.log('Message updated:', payload);
            setMessages(prev =>
              prev.map(msg =>
                msg.id === payload.new.id ? { ...msg, ...payload.new } : msg
              )
            );
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [conversationId, fetchMessages]);

  const refetch = () => {
    fetchMessages();
  };

  return { messages, loading, error, refetch };
}
